// PageContext — lightweight broadcaster for "what is the user looking at
// right now". Pages with a detail/drawer/modal view call setFocus({...})
// when something becomes the centre of attention so Mo (the ⌘M chat)
// can answer "summarise this" / "what's wrong with this PO" / "compare
// to last week" without the user having to re-paste the entity.
//
// Usage from a page:
//
//   import { usePageContext } from '../lib/PageContext';
//   const page = usePageContext();
//   useEffect(() => {
//     if (selectedReport) {
//       page.setFocus({ type: 'report', id: selectedReport.id, label: selectedReport.title });
//       return () => page.setFocus(null);
//     }
//   }, [selectedReport]);
//
// App.jsx reads `focus` and includes it in the appContext sent to the
// assistant-chat Lambda. The Lambda turns it into a one-line system
// prompt addition.

import { createContext, useCallback, useContext, useState } from 'react';

const PageContextCtx = createContext(null);

export function usePageContext() {
  const ctx = useContext(PageContextCtx);
  if (!ctx) {
    // Don't throw — pages should remain usable without the provider.
    // The chat just doesn't get extra context. Returning a no-op shape
    // means callers don't need to defensively check for the provider.
    return { focus: null, setFocus: () => {} };
  }
  return ctx;
}

export function PageContextProvider({ children }) {
  const [focus, setFocusState] = useState(null);

  // Stable callback so pages can put it in useEffect deps without
  // triggering an infinite re-broadcast loop.
  const setFocus = useCallback((next) => {
    setFocusState(prev => {
      // Cheap reference-equality skip — if a page re-broadcasts the
      // same object, don't churn React state.
      if (prev === next) return prev;
      if (prev && next && prev.type === next.type && prev.id === next.id && prev.label === next.label) return prev;
      return next;
    });
  }, []);

  return (
    <PageContextCtx.Provider value={{ focus, setFocus }}>
      {children}
    </PageContextCtx.Provider>
  );
}
